import { ModuleFE_Advisor_EHREnvironment, ModuleFE_Advisor_Facility, ModuleFE_Advisor_Organization, ModuleFE_AdvisorSession, ModuleFE_EMInterface, ModuleFE_EncounterMetadata, SessionKey_EHREnvironmentId, SessionKey_FacilityId, SessionKey_OrganizationId, SessionKey_Provider, SessionKey_ProviderId, StorageKey_SnapShotVersion } from '@app/advisor-frontend';
import { asArray, compare, currentTimeMillis, exists, md5, Module, scrub } from '@nu-art/ts-common';
import { getBaseAnalyticsMetadata } from '@nu-art/analytics-frontend/_utils/get-metadata';
import { ModuleFE_Analytics } from '@nu-art/analytics-frontend';
import { ModuleFE_AdvisorMini } from '../modules/ModuleFE_AdvisorMini.js';
import { dispatch_OnAdvisorAnalyticsEventSent } from './dispatch.js';
class ModuleFE_AdvisorAnalytics_Class extends Module {
    //######################### Properties #########################
    sessionId;
    lastEventKey = undefined;
    triggerTrack = {};
    __onAdvisorReset = () => {
        delete this.triggerTrack.app_reportCC;
    };
    //######################### Internal Logic #########################
    getSessionId = () => {
        if (!this.sessionId) {
            const encounterId = ModuleFE_AdvisorSession.getCurrentEncounterId();
            const providerId = ModuleFE_AdvisorSession.getSessionProviderId();
            this.sessionId = md5(`${encounterId}.${providerId}`);
        }
        return this.sessionId;
    };
    collectAnalyticsContext = () => {
        const baseContext = getBaseAnalyticsMetadata();
        const encounterMetadata = ModuleFE_EncounterMetadata.getEncounterMetadata();
        return {
            ...baseContext,
            artifactVersion: StorageKey_SnapShotVersion.get() ?? encounterMetadata?.artifactMetadata.dataVersion ?? 'N/A',
            isMiniMode: ModuleFE_AdvisorMini.getIsMiniActive(),
            encounterId: ModuleFE_AdvisorSession.getCurrentEncounterId(),
        };
    };
    collectAnalyticsMetadata = () => {
        return {
            userId: SessionKey_ProviderId.get(),
            sessionId: this.getSessionId(),
            environmentId: SessionKey_EHREnvironmentId.get(),
            facilityId: SessionKey_FacilityId.get(),
            organizationId: SessionKey_OrganizationId.get(),
        };
    };
    sendAdvisorAnalyticsEvent = async (eventKey, eventProps, sendProps) => {
        if (ModuleFE_EMInterface.isAvatarMode())
            return;
        if (sendProps?.singleTrigger && this.eventsHaveTriggered(eventKey))
            return;
        if (sendProps?.skipIfUnchanged && compare(this.triggerTrack[eventKey]?.props, eventProps))
            return;
        if (sendProps?.promoteEncounter)
            ModuleFE_EMInterface.promoteEncounterToViewed();
        if (sendProps?.recordView)
            ModuleFE_EMInterface.recordPractitionerView();
        const previousTrigger = this.triggerTrack[eventKey];
        try {
            const metadata = this.collectAnalyticsMetadata();
            const context = this.collectAnalyticsContext();
            const event = {
                key: eventKey,
                timestamp: currentTimeMillis(),
                userId: metadata.userId,
                sessionId: metadata.sessionId,
                groups: scrub({
                    organizationId: metadata.organizationId,
                    facilityId: metadata.facilityId,
                    environmentId: metadata.environmentId,
                }),
                context: context,
            };
            if (exists(eventProps))
                event.properties = eventProps;
            this.lastEventKey = eventKey;
            this.triggerTrack[eventKey] = { timestamp: Date.now(), props: eventProps };
            await ModuleFE_Analytics._v1.sendEvent({ event }).executeSync();
            dispatch_OnAdvisorAnalyticsEventSent.dispatchAll(eventKey);
            this.logDebug(`Successfully send event with key ${eventKey}`);
        }
        catch (err) {
            this.triggerTrack[eventKey] = previousTrigger;
            this.logError(`Failed to send event with key ${eventKey}`, err);
        }
    };
    //######################### Public Logic #########################
    updateLexicon = async () => {
        const facilityId = SessionKey_FacilityId.get();
        const facility = ModuleFE_Advisor_Facility.cache.unique(facilityId);
        if (!facility)
            return this.logError(`Running with facility id ${facilityId} that is not in the artifact`);
        const environmentId = SessionKey_EHREnvironmentId.get();
        const environment = ModuleFE_Advisor_EHREnvironment.cache.unique(environmentId);
        if (!environment)
            return this.logError(`Running with environment id ${environmentId} that is not in the artifact`);
        const organization = ModuleFE_Advisor_Organization.cache.unique(facility.organizationId);
        if (!organization)
            return this.logError(`Running with organization id ${facility.organizationId} that is not in the artifact`);
        try {
            await ModuleFE_Analytics._v1.updateLexicon({
                mode: 'set_once',
                lexiconMap: {
                    organizationId: { id: organization._id, label: organization.name },
                    environmentId: { id: environment._id, label: environment.label },
                    facilityId: { id: facility._id, label: facility.name },
                }
            }).executeSync();
        }
        catch (err) {
            this.logError('Failed to update lexicons', err);
        }
    };
    updateUser = async () => {
        const userId = SessionKey_ProviderId.get();
        const userData = SessionKey_Provider.get();
        if (!userId || !userData)
            return this.logError('No user data in session!');
        try {
            await ModuleFE_Analytics._v1.updateUser({
                mode: 'set_once',
                userData: {
                    userId: userId,
                    firstName: userData.first_name,
                    lastName: userData.last_name,
                    displayName: userData.display_name,
                }
            }).executeSync();
        }
        catch (err) {
            this.logError('Failed to update user', err);
        }
    };
    trigger = {
        general: {
            app_launch: (props) => this.sendAdvisorAnalyticsEvent('app_launch', props, { singleTrigger: true }),
            app_reportCC: props => this.sendAdvisorAnalyticsEvent('app_reportCC', props, { skipIfUnchanged: true }),
        },
        faq: {
            faq_open: () => this.sendAdvisorAnalyticsEvent('faq_open', undefined, { promoteEncounter: true, recordView: true }),
            faq_questionOpen: props => this.sendAdvisorAnalyticsEvent('faq_questionOpen', props, { recordView: true }),
        },
        bugReport: {
            bugReport_open: () => this.sendAdvisorAnalyticsEvent('bugReport_open', undefined, { recordView: true }),
            bugReport_abandon: () => this.sendAdvisorAnalyticsEvent('bugReport_abandon', undefined, { recordView: true }),
            bugReport_submit: () => this.sendAdvisorAnalyticsEvent('bugReport_submit', undefined, { recordView: true }),
        },
        cockpit: {
            cockpit_open: () => this.sendAdvisorAnalyticsEvent('cockpit_open', undefined, { promoteEncounter: true, recordView: true }),
            cockpit_ddxFocus: props => this.sendAdvisorAnalyticsEvent('cockpit_ddxFocus', props, { recordView: true }),
            cockpit_widgetFocus: props => this.sendAdvisorAnalyticsEvent('cockpit_widgetFocus', props, { recordView: true }),
        },
        addData: {
            addData_ddx_open: props => this.sendAdvisorAnalyticsEvent('addData_ddx_open', props, { promoteEncounter: true, recordView: true }),
            addData_ddx_abandon: () => this.sendAdvisorAnalyticsEvent('addData_ddx_abandon', undefined, { recordView: true }),
            addData_ddx_submit: props => this.sendAdvisorAnalyticsEvent('addData_ddx_submit', props, { recordView: true }),
            addData_order_open: () => this.sendAdvisorAnalyticsEvent('addData_order_open', undefined, { promoteEncounter: true, recordView: true }),
            addData_order_abandon: () => this.sendAdvisorAnalyticsEvent('addData_order_abandon', undefined, { promoteEncounter: true, recordView: true }),
            addData_order_submit: props => this.sendAdvisorAnalyticsEvent('addData_order_submit', props, { recordView: true }),
        },
        ddx: {
            ddx_hover: props => this.sendAdvisorAnalyticsEvent('ddx_hover', props),
        },
        orderCart: {
            orderCart_open: props => this.sendAdvisorAnalyticsEvent('orderCart_open', props, { promoteEncounter: true, recordView: true }),
            orderCart_submit: props => this.sendAdvisorAnalyticsEvent('orderCart_submit', props, { promoteEncounter: true, recordView: true }),
            orderCart_abandon: props => this.sendAdvisorAnalyticsEvent('orderCart_abandon', props, { recordView: true }),
        },
        settings: {
            settings_open: () => this.sendAdvisorAnalyticsEvent('settings_open', undefined, { recordView: true }),
            settings_changed: props => this.sendAdvisorAnalyticsEvent('settings_changed', props, { recordView: true }),
        },
        findings: {
            findings_viewSelect: props => this.sendAdvisorAnalyticsEvent('findings_viewSelect', props, { promoteEncounter: true, recordView: true }),
            findings_tooltipOpen: props => this.sendAdvisorAnalyticsEvent('findings_tooltipOpen', props),
            findings_open: props => this.sendAdvisorAnalyticsEvent('findings_open', props, {
                promoteEncounter: props.method === 'manual',
                recordView: props.method === 'manual',
            }),
        },
        inbox: {
            inbox_orderClick: props => this.sendAdvisorAnalyticsEvent('inbox_orderClick', props, { promoteEncounter: true, recordView: true }),
            inbox_filter: props => this.sendAdvisorAnalyticsEvent('inbox_filter', props, { promoteEncounter: true, recordView: true }),
            inbox_open: () => this.sendAdvisorAnalyticsEvent('inbox_open', undefined, { promoteEncounter: true, recordView: true }),
        },
        orders: {
            order_placed: props => this.sendAdvisorAnalyticsEvent('order_placed', props, { promoteEncounter: true, recordView: true }),
        },
        navigation: {
            navigation_protocolOpened: props => this.sendAdvisorAnalyticsEvent('navigation_protocolOpened', props, { promoteEncounter: true, recordView: true }),
            navigation_advance: props => this.sendAdvisorAnalyticsEvent('navigation_advance', props, { promoteEncounter: true, recordView: true }),
            navigation_provideInfo: props => this.sendAdvisorAnalyticsEvent('navigation_provideInfo', props, { promoteEncounter: true, recordView: true }),
            navigation_goto_open: props => this.sendAdvisorAnalyticsEvent('navigation_goto_open', props, { promoteEncounter: true, recordView: true }),
            navigation_goto_abandon: () => this.sendAdvisorAnalyticsEvent('navigation_goto_abandon', undefined, { recordView: true }),
            navigation_goto_submit: () => this.sendAdvisorAnalyticsEvent('navigation_goto_submit', undefined, { recordView: true }),
            navigation_considerationSwitch: props => this.sendAdvisorAnalyticsEvent('navigation_considerationSwitch', props, {
                promoteEncounter: true,
                recordView: true
            }),
            navigation_OrderAll: props => this.sendAdvisorAnalyticsEvent('navigation_OrderAll', props, { promoteEncounter: true, recordView: true }),
        },
        wizard: {
            wizard__open: props => this.sendAdvisorAnalyticsEvent('wizard__open', props, { promoteEncounter: true, recordView: true }),
            wizard__submitStep: props => this.sendAdvisorAnalyticsEvent('wizard__submitStep', props, { recordView: true }),
            wizard__complete: props => this.sendAdvisorAnalyticsEvent('wizard__complete', props, { recordView: true }),
            wizard__abandon: props => this.sendAdvisorAnalyticsEvent('wizard__abandon', props, { recordView: true }),
        },
        view: {
            view__hpi: () => this.sendAdvisorAnalyticsEvent('view__hpi', undefined, { promoteEncounter: true, recordView: true }),
            view__suggestedWorkup: () => this.sendAdvisorAnalyticsEvent('view__suggestedWorkup', undefined, { promoteEncounter: true, recordView: true }),
            view__liveMDM: props => this.sendAdvisorAnalyticsEvent('view__liveMDM', props, {
                promoteEncounter: props.method === 'manual',
                recordView: props.method === 'manual',
            }),
            view__homeHistory: () => this.sendAdvisorAnalyticsEvent('view__homeHistory', undefined, { promoteEncounter: true, recordView: true }),
            view__chiefComplaint: () => this.sendAdvisorAnalyticsEvent('view__chiefComplaint', undefined, { promoteEncounter: true, recordView: true }),
            view__keyDDX: (props) => this.sendAdvisorAnalyticsEvent('view__keyDDX', props, {
                promoteEncounter: props.method === 'manual',
                recordView: props.method === 'manual'
            }),
            view_vitals: props => this.sendAdvisorAnalyticsEvent('view_vitals', props, { promoteEncounter: true, recordView: true }),
            view_utilityMenu: () => this.sendAdvisorAnalyticsEvent('view_utilityMenu', undefined, { recordView: true }),
        },
        note: {
            note_open: props => this.sendAdvisorAnalyticsEvent('note_open', props, { promoteEncounter: true, recordView: true }),
            note_edit: () => this.sendAdvisorAnalyticsEvent('note_edit', undefined, { recordView: true }),
            note_send: props => this.sendAdvisorAnalyticsEvent('note_send', props, { recordView: true }),
            note_abandon: props => this.sendAdvisorAnalyticsEvent('note_abandon', props, { recordView: true }),
            note_copy: props => this.sendAdvisorAnalyticsEvent('note_copy', props, { recordView: true }),
            note_disposition_open: props => this.sendAdvisorAnalyticsEvent('note_disposition_open', props, { recordView: true }),
            note_disposition_submit: props => this.sendAdvisorAnalyticsEvent('note_disposition_submit', props, { recordView: true }),
            note_disposition_abandon: props => this.sendAdvisorAnalyticsEvent('note_disposition_abandon', props, { recordView: true }),
            note_disposition_select: props => this.sendAdvisorAnalyticsEvent('note_disposition_select', props, { recordView: true }),
            note_template_select: props => this.sendAdvisorAnalyticsEvent('note_template_select', props, { recordView: true }),
        },
        uiLoad: {
            uiLoad_start: props => this.sendAdvisorAnalyticsEvent('uiLoad_start', props, { singleTrigger: true }),
            uiLoad_artifact: props => this.sendAdvisorAnalyticsEvent('uiLoad_artifact', props),
            uiLoad_protocols: (props) => this.sendAdvisorAnalyticsEvent('uiLoad_protocols', props, { singleTrigger: true }),
            uiLoad_hpi: (props) => this.sendAdvisorAnalyticsEvent('uiLoad_hpi', props, { singleTrigger: true }),
            uiLoad_findings: (props) => this.sendAdvisorAnalyticsEvent('uiLoad_findings', props, { singleTrigger: true }),
            uiLoad_inbox: (props) => this.sendAdvisorAnalyticsEvent('uiLoad_inbox', props, { singleTrigger: true }),
            uiLoad_OrderCart: (props) => this.sendAdvisorAnalyticsEvent('uiLoad_OrderCart', props, { singleTrigger: true }),
            uiLoad_liveMdm: (props) => this.sendAdvisorAnalyticsEvent('uiLoad_liveMdm', props, { singleTrigger: true }),
            uiLoad_keyScores: (props) => this.sendAdvisorAnalyticsEvent('uiLoad_keyScores', props, { singleTrigger: true }),
        },
        recap: {
            catchupRecap_viewed: props => this.sendAdvisorAnalyticsEvent('catchupRecap_viewed', props, { singleTrigger: true }),
            catchupRecap_dismissed: () => this.sendAdvisorAnalyticsEvent('catchupRecap_dismissed', undefined, { recordView: true, promoteEncounter: true }),
        },
        eventLog: {
            eventLog_opened: props => this.sendAdvisorAnalyticsEvent('eventLog_opened', props, { promoteEncounter: true, recordView: true }),
            eventLog_itemExpanded: props => this.sendAdvisorAnalyticsEvent('eventLog_itemExpanded', props, { promoteEncounter: true, recordView: true }),
            eventLog_itemClicked: props => this.sendAdvisorAnalyticsEvent('eventLog_itemClicked', props, { promoteEncounter: true, recordView: true }),
        },
    };
    getLastEventKey = () => this.lastEventKey;
    eventsHaveTriggered = (eventKeys) => {
        const keys = asArray(eventKeys);
        return keys.every(key => exists(this.triggerTrack[key]));
    };
    getTriggerTime = (eventKey) => this.triggerTrack[eventKey]?.timestamp;
    getTimeSinceLoad = () => {
        const loadTime = this.getTriggerTime('uiLoad_start');
        if (!loadTime)
            return 0;
        return Date.now() - loadTime;
    };
}
export const ModuleFE_AdvisorAnalytics = new ModuleFE_AdvisorAnalytics_Class();
//# sourceMappingURL=consts.js.map